import { useEffect, useMemo, useReducer } from 'react'
import { Link } from 'react-router-dom'
import { AdvancedImage, placeholder, responsive } from '@cloudinary/react'
import styles from '../../SharedCalculatorCss.module.scss'
import { InputTextLabeled } from '../../../UI/inputs/InputTextLabeled/InputTextLabeled'
import { ButtonByAdam } from '../../../UI/buttons/ButtonByAdam/ButtonByAdam'
import { HeaderWithIcon } from '../../../UI/headers/CustomHeader/CustomHeader'
import { ErrorListMessage } from '../../../UI/MessageComponents/ErrorListMessage/ErrorListMessage'
import { SuccessMessage } from '../../../UI/MessageComponents/SuccessMessage/SuccessMessage'
import {
  initialRoundedPortionsReducer,
  roundedCakeReducer,
  RoundedPortionsReducerActionName
} from './RoundedPortionsReducer'
import { CalculatorOperations } from '../../../../helpers/cake'
import { Select } from '../../../UI/inputs/Select/Select'
import { frostingIngredients } from '../../../../helpers/dictionaries'
import { SuccessListMessage } from '../../../UI/MessageComponents/SuccessListMessage/SuccessListMessage'
import { SubscriptionType } from '../../../../service/Product/ProductService'
import { ErrorMessage } from '../../../UI/MessageComponents/ErrorMessage/ErrorMessage'
import { translateSubscriptionType } from '../../../../helpers/utils'
import { useCloudinary } from '../../../../hooks/useCloudinary'

interface Props {
  countAutomatic: boolean
  isAvailableForUser: boolean
  subType: SubscriptionType
}

export const RoundedCake = ({
  countAutomatic,
  isAvailableForUser,
  subType
}: Props): JSX.Element => {
  const [calculator, dispatch] = useReducer(
    roundedCakeReducer,
    initialRoundedPortionsReducer
  )

  const { getImageInstance } = useCloudinary()

  const roundedCakeImage = useMemo(() => {
    const imageInstance = getImageInstance(
      'szkolamajcake/kalkulatory/tort_okragly_wzor'
    )

    return (
      <AdvancedImage
        cldImg={imageInstance}
        plugins={[responsive(), placeholder()]}
      />
    )
  }, [])

  const calculate = (): void =>
    dispatch({ type: RoundedPortionsReducerActionName.CALCULATE_RESULT })
  const updateCalculatorData = (data: CalculatorOperations): void =>
    dispatch({
      type: RoundedPortionsReducerActionName.UPDATE_ROUNDED_PORTIONS_CALC_DATA,
      data
    })

  useEffect((): void => {
    if (countAutomatic && isAvailableForUser) {
      calculate()
    }
  }, [calculator.d, calculator.height, calculator.chosenIcing])

  return (
    <section className={styles.CalculatorContainer}>
      <div className={styles.Content_Inputs}>
        <HeaderWithIcon headerType='typical'>Torty Okrągłe</HeaderWithIcon>

        <InputTextLabeled
          type='number'
          titleColor='pink'
          title='Podaj średnicę tortu'
          dataRequired
          disabled={!isAvailableForUser}
          value={calculator.d}
          placeholder='Podaj średnicę tortu'
          onChangeHandler={(val): void =>
            updateCalculatorData({ fieldName: 'd', fieldValue: val })
          }
        />

        <InputTextLabeled
          type='number'
          titleColor='blue'
          title='Podaj wysokość tortu'
          dataRequired
          disabled={!isAvailableForUser}
          value={calculator.height}
          placeholder='Podaj wysokość tortu'
          onChangeHandler={(val): void =>
            updateCalculatorData({ fieldName: 'height', fieldValue: val })
          }
        />

        <Select
          label='Wybierz rodzaj kremu'
          value={calculator.chosenIcing}
          placeholder='Wybierz rodzaj kremu'
          disabled={!isAvailableForUser}
          options={frostingIngredients}
          onChange={(val): void =>
            updateCalculatorData({
              fieldName: 'chosenIcing',
              fieldValue: val as string
            })
          }
        />

        {!isAvailableForUser && (
          <div className={styles.SubscriptionErrorMessage}>
            <ErrorMessage>
              Z kalkulatora mogą korzystać tylko użytkownicy o poziomie
              subskrypcji <strong>{translateSubscriptionType(subType)}</strong>.
              <strong>
                <Link to='/subskrypcje'>
                  {' '}
                  Zapoznaj się z dostępnymi możliwościami
                </Link>
              </strong>
            </ErrorMessage>
          </div>
        )}

        {!countAutomatic && (
          <ButtonByAdam
            disabled={!isAvailableForUser}
            btnType='premium'
            onClick={(): void => calculate()}
          >
            Oblicz
          </ButtonByAdam>
        )}

        <div className={styles.ResultContainer}>
          {calculator.errors.length > 0 && (
            <ErrorListMessage
              title='Błędne wartości w kalkulatorze'
              errorList={calculator.errors}
            />
          )}
          {calculator.result && (
            <SuccessMessage>
              {'Na ten tort przewidujemy '}
              <strong>{calculator.result}</strong>
              {'g kremu '}
            </SuccessMessage>
          )}
          {calculator.ingredients.length > 0 && (
            <SuccessListMessage
              title='Skład kremu'
              successList={calculator.ingredients}
            />
          )}
        </div>
      </div>

      <div className={styles.Content_Image}>{roundedCakeImage}</div>
    </section>
  )
}
